import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
CircularProgress,
Tabs,
Tab,
Alert
} from '@mui/material';
import { 
Delete, 
Inventory,
PictureAsPdf,
TableChart, 
GetApp,
Add,
Settings,
TrendingUp,
Warning,
CheckCircle,
Error,
AttachMoney,
} from '@mui/icons-material';
import {
X,
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
APIIngredientInventoryList,APIIngredientInventoryStore,
APIIngredientInventoryUpdate,APIIngredientInventoryDelete,
APISupplierNames,APIIngredientNames,} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
formatCreatedAtDate, formatNumberWithComma, 
    formatNumberWithComma2, 
getCategoriesNameById, getUserDetails, 
recordsPerPage, } from '../../../utils/helpers';
import {
exportToCSV,
exportToExcel,
exportToPDF
} from '../../../services/restaurant/InventoryService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const IngredientInventory = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);
const [referenceData, setReferenceData] = useState({
    branchNames: [],
    ingredientsNames: [],
    supplierNames: []
});
const [pageState, setPageState] = useState({
    pageData: [],
    supplierNamesData: [],
    stockNamesData: [],
    stockStats: {
    total_stock_items: 0,
    total_quantity: 0,
    total_balance: 0,
    total_value: 0,
    total_selling_value: 0,
    active_stock: 0,
    out_of_stock: 0,
    low_stock: 0,
    normal_stock: 0,
    high_stock: 0,
    potential_profit: 0,
    most_valuable_item: 'N/A',
    most_valuable_value: 0
    }
});

const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');

const [formData, setFormData] = useState({
    supplier_id: '',
    counter_name_id:'',
    stock_id: '',
    quantity: '',
    cost: '',
    selling: '',
    stock_date: '',
    description: ''
});

const [filters, setFilters] = useState({
    supplier_id: '',
    dateFrom: '',
    dateTo: ''
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// Tab state for Add/Edit dialog
const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
    fetchReferenceData();
}, []);


const fetchReferenceData = async () => {
    try {
    const [branchesResult, ingRes, supRes] = await Promise.all([
        apiService.branchNames(),
        apiService.listNames(APIIngredientNames),
        apiService.listNames(APISupplierNames),
    ]);

    setReferenceData({
        branchNames: branchesResult.success ? branchesResult.data : [],
        ingredientsNames: ingRes.success ? ingRes.data : [],
        supplierNames: supRes.success ? supRes.data : [],
    });
    } catch (error) {
    showToast("Error", "Failed to load reference data", "error");
    }
};

const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
        // For super admin, use selected branch or current user's branch
        let branchIdToUse = branchId;
        if (!hasPermission(PERMISSIONS.BRANCHES)) {
            branchIdToUse = getUserDetails("branchId");
        }

        // Use the centralized API service
        const result = await apiService.list(branchIdToUse, APIIngredientInventoryList);

        if (result.success) {
            const data = Array.isArray(result.data) ? result.data.reverse() : [];

            setPageState(prev => ({
            ...prev,
            pageData: data
            }));

            showToast("Success", result.message || TEXT_DATA_LOADED, "success");
        } else {
            setError(result.message);
            showToast("Error", result.message, "error");
        }
        } catch (error) {
        let errorMessage = "Failed to load data";
        if (error.code === "ERR_NETWORK") {
            errorMessage = TEXT_CONNECTION_ERROR;
        }
        setError(errorMessage);
        showToast("Error", errorMessage, "error");
        } finally {
        setLoading(false);
        }
};


const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
};

// Filter stocks based on search term and filters
const filteredData = pageState.pageData.filter(stock => {
    const matchesSearch = searchTerm === '' ||
    stock.stock_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.description?.toLowerCase().includes(searchTerm.toLowerCase());

    // Supplier filter
    const matchesSupplier = !filters.supplier_id || 
    stock.supplier_id === filters.supplier_id;

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && stockDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && stockDate <= filterToDate;
    }

    return matchesSearch && matchesSupplier && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    supplier_id: '',
    stock_id: '',
    quantity: '',
    cost: '',
    selling: '',
    stock_date: new Date().toISOString().split('T')[0],
    description: ''
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
    setActiveTab(0);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    supplier_id: record.supplier_id,
    stock_id: record.stock_id,
    quantity: record.quantity,
    cost: record.cost,
    selling: record.selling,
    stock_date: new Date(record.stock_date).toISOString().split('T')[0],
    description: record.description || ''
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};


// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    supplier_id: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = referenceData.branchNames.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

// Tab navigation
const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const handleNextTab = () => {
    if (activeTab < 2) {
    setActiveTab(activeTab + 1);
    }
};

const handlePrevTab = () => {
    if (activeTab > 0) {
    setActiveTab(activeTab - 1);
    }
};

const handleExportClick = (event) => {
  setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
  setExportAnchorEl(null);
};

const handleExportExcel = async () => {
  try {
    handleExportClose();
    await exportToExcel(filteredData, {
      supplierNamesData: referenceData.supplierNames,
      stockNamesData: referenceData.ingredientsNames,
      branchNamesFilterData: referenceData.branchNames
    }, filters);
    showToast("Success", "Excel file downloaded successfully", "success");
  } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportPDF = async () => {
  try {
    handleExportClose();
    await exportToPDF(filteredData, {
      supplierNamesData: referenceData.supplierNames,
      stockNamesData: referenceData.ingredientsNames,
      branchNamesFilterData: referenceData.branchNames
    }, filters);
    showToast("Success", "PDF file downloaded successfully", "success");
  } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportCSV = async () => {
  try {
    handleExportClose();
    await exportToCSV(filteredData, {
      supplierNamesData: referenceData.supplierNames,
      stockNamesData: referenceData.ingredientsNames,
      branchNamesFilterData: referenceData.branchNames
    });
    showToast("Success", "CSV file downloaded successfully", "success");
  } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
  }
};

const validateForm = () => {
    const errors = [];

    if (!formData.supplier_id) errors.push('Supplier is required');
    if (!formData.stock_id) errors.push('Stock item is required');
    if (!formData.quantity) errors.push('Quantity is required');
    if (parseInt(formData.quantity) <= 0) errors.push('Quantity must be greater than 0');
    if (!formData.cost) errors.push('Cost is required');
    if (parseFloat(formData.cost) <= 0) errors.push('Cost must be greater than 0');
    if (!formData.selling) errors.push('Selling price is required');
    if (parseFloat(formData.selling) <= 0) errors.push('Selling price must be greater than 0');
    if (!formData.stock_date) errors.push('Stock date is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
        if (!validateForm()) return;
    }

    const formDataToSend = new FormData();

    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
        formDataToSend.append('branch_id', selectedBranch);
    } else {
        formDataToSend.append('branch_id', getUserDetails("branchId"));
    }

    // Add user_id (current user)
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Add form data
    formDataToSend.append('supplier_id', formData.supplier_id);
    formDataToSend.append('stock_id', formData.stock_id);
    formDataToSend.append('quantity', formData.quantity);
    formDataToSend.append('cost', formData.cost);
    formDataToSend.append('selling', formData.selling);
    formDataToSend.append('stock_date', formData.stock_date);
    formDataToSend.append('description', formData.description || '');

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
    }

    try {
        let result;

        if (postStatus === "ADD") {
        result = await apiService.create(APIIngredientInventoryStore, formDataToSend);
        } else if (postStatus === "UPDATE") {
        result = await apiService.update(APIIngredientInventoryUpdate, formDataToSend);
        } else if (postStatus === "DELETE") {
        result = await apiService.delete(APIIngredientInventoryDelete, formDataToSend);
        }

        // Check success based on the service response
        if (result.success) {
        showToast("Success", result.message, "success");
        handleCloseAdd();
        fetchData();
        } else {
        showToast("Error", result.message, "error");
        }
    } catch (error) {
        console.log("error"+error)
        showToast("Error", "An unexpected error occurred \n\n" + error.message, "error");
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Ingredients Inventory Management ( {formatNumberWithComma2(pageState.pageData.length)} Total Items )
    </Typography>
    {/* Display selected branch name */}
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Inventory
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Select Branch
            </Button>
            )}
    
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>
            {/* Add Export Data button here */}
            {hasPermission(PERMISSIONS.EXPORT) && (
                <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                    textTransform: 'none',
                    borderRadius: '8px'
                }}
                >
                Export Data
                </Button>
            )}

            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={() => {
                fetchData();
            }}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {( filters.supplier_id || filters.dateFrom || filters.dateTo) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.supplier_id && (
            <Chip
                label={`Supplier: ${getCategoriesNameById(filters.supplier_id, referenceData.supplierNames)}`}
                onDelete={() => setFilters(prev => ({ ...prev, supplier_id: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Stock Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Stock Item</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Selling</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((stock, index) => (
                <TableRow
                    key={index}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {new Date(stock.stock_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                    {getCategoriesNameById(stock.supplier_id, referenceData.supplierNames) || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                    {stock.stock_name || 'N/A'}
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={formatNumberWithComma2(stock.quantity)}
                        color="default"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={formatNumberWithComma(stock.cost)}
                        color="success"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={formatNumberWithComma(stock.selling)}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {getCategoriesNameById(stock.branch_id, referenceData.branchNames) || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(stock)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(stock)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(stock)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Inventory sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No inventory items found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new inventory item</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {referenceData.branchNames.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter inventory only from that branch. 
            Leave unselected to view all inventory.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                handleBranchSelect(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
    anchorEl={exportAnchorEl}
    open={Boolean(exportAnchorEl)}
    onClose={handleExportClose}
    anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
    }}
    transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
    }}
    >
    <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
    </MenuItem>
    <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
    </MenuItem>
    <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
    </MenuItem>
    </Menu>
    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Inventory
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Inventory
            </Button>

            <Divider>OR</Divider>

            <FormControl fullWidth>
            <InputLabel>Supplier</InputLabel>
            <Select
                name="supplier_id"
                value={filters.supplier_id}
                onChange={handleFilterChange}
                label="Supplier"
            >
                <MenuItem value="">All Suppliers</MenuItem>
                {referenceData.supplierNames.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>


            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Stock Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog with Tabs */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ 
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "Add New Inventory Item" : "Edit Inventory Item"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="fullWidth"
            >
                <Tab 
                label="Basic Info" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Pricing" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Details" 
                sx={{ textTransform: 'none' }}
                />
            </Tabs>
            </Box>

            {/* Tab 1: Basic Info */}
            {activeTab === 0 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Supplier *</InputLabel>
                    <Select
                        name="supplier_id"
                        value={formData.supplier_id}
                        onChange={handleInputChange}
                        label="Supplier *"
                    >
                        <MenuItem value="">
                        <em>Select supplier</em>
                        </MenuItem>
                        {referenceData.supplierNames.map((supplier) => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                        </MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Stock Item *</InputLabel>
                    <Select
                        name="stock_id"
                        value={formData.stock_id}
                        onChange={handleInputChange}
                        label="Stock Item Name *"
                    >
                        <MenuItem value="">
                        <em>Select stock item</em>
                        </MenuItem>

                        {referenceData.ingredientsNames.map((stock) => (
                            <MenuItem key={stock.id} value={stock.id}>
                            {stock.name}
                            </MenuItem>
                        ))
                        }
                    </Select>
                    </FormControl>
                </Grid>
                </Box>
            </Box>
            )}

            {/* Tab 2: Pricing */}
            {activeTab === 1 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Quantity *"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Cost Price *"
                    name="cost"
                    type="number"
                    value={formData.cost}
                    onChange={handleInputChange}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Selling Price *"
                    name="selling"
                    type="number"
                    value={formData.selling}
                    onChange={handleInputChange}
                    required
                    />
                </Grid>
                </Box>
            </Box>
            )}

            {/* Tab 3: Details */}
            {activeTab === 2 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Stock Date *"
                    type="date"
                    name="stock_date"
                    value={formData.stock_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    />
                </Grid>
                </Box>
            </Box>
            )}
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
            {activeTab > 0 && (
            <Button
                color="primary"
                onClick={handlePrevTab}
                variant="outlined"
                sx={{ mr: 1 }}
            >
                Previous
            </Button>
            )}
        </Box>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
            {activeTab < 2 ? (
            <Button
                color="primary"
                onClick={handleNextTab}
                variant="contained"
            >
                Next
            </Button>
            ) : (
            <Button
                type="submit"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
            >
                {postStatus === "ADD" ? "Add Inventory" : "Update Inventory"}
            </Button>
            )}
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Inventory Item Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Supplier:</b> {getCategoriesNameById(selectedRecord.supplier_id, referenceData.supplierNames)}</Typography>
                </Grid>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Stock Item:</b> {selectedRecord.stock_name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Quantity:</b> {formatNumberWithComma(selectedRecord.quantity)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Cost Price:</b> {formatNumberWithComma(selectedRecord.cost)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Selling Price:</b> {formatNumberWithComma(selectedRecord.selling)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Stock Date:</b> {new Date(selectedRecord.stock_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Branch:</b> {getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}</Typography>
                </Grid>
            </Grid>
            <br/>
                {selectedRecord.description && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2"><b>Description:</b> {selectedRecord.description}</Typography>
                </Grid>
                )}
            <br/>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Inventory Item</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this inventory item?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedRecord.stock_name || 'N/A'}
            </Typography>
            <Typography variant="body2">
                Quantity: {formatNumberWithComma(selectedRecord.quantity)}
            </Typography>
            <Typography variant="body2">
                Branch: {getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
                Deleting this item will remove it from the inventory permanently.
            </Alert>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default IngredientInventory;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});